import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';
import { t, useUiLocale } from '@/i18n/locale';
import { CompanionDesign } from './CompanionDesign';
/* Isolated author workspace. Reuses production UI; no auth, provider or business writes. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, MessageCircle, Palette, ShieldCheck, Check } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { createCompanionReviewApi } from './companionReviewApi';
import { CompanionSession } from './CompanionSession';
import { CompanionRoom, CompanionHelp } from './CompanionRoom';
import { CompanionParentControls } from './CompanionRoutes';
import {
  memoryInputSchema,
  type BuddyApi,
  type BuddySnapshot,
  type BuddyChatInput,
  type BuddyVoiceSettings,
} from './companionApi';

export const COMPLETE_BUDDY_STORY = `The Moon Rover’s Pocket Map

1. A little idea
Nova built a tiny rover with a pocket for collecting moon stones. On its first trip, it found a star that had lost the way home.

2. A quiet clue
The rover stopped beside a crater. Instead of rushing on, it listened. Three distant lights were blinking: gold, blue, then coral.

3. One step together
Nova drew the lights on a map and asked the rover to follow them one at a time. When the second path was blocked, they tried a smaller bridge together.

4. Room for tomorrow
The star reached its cloud window. It gave the rover a silver pencil. Nova tucked it beside the map and left one empty page for their next adventure.`;
const initialMessages = [
  { role: 'user' as const, content: 'I want to build a little moon rover and give it a story.' },
  { role: 'assistant' as const, content: COMPLETE_BUDDY_STORY },
];
// eslint-disable-next-line react-refresh/only-export-components -- shared companion contract and hooks
export function createCompanionPreviewApi({
  contractFixtures = false,
}: { contractFixtures?: boolean } = {}): BuddyApi {
  let state: BuddySnapshot = {
    revision: 1,
    memory_enabled: true,
    profile: {
      name: 'Nova',
      character: 'plush',
      hat: 'sailor',
      earStyle: 'up',
      room: 'sky',
      accessories: ['satchel'],
    },
    memories: [
      {
        id: '11111111-1111-4111-8111-111111111111',
        text: 'I like stories about space.',
        category: 'interest',
        source: 'explicit',
        created_at: '2026-11-01T00:00:00.000Z',
        updated_at: '2026-11-01T00:00:00.000Z',
        forgotten_at: null,
      },
    ],
    forgotten: [],
  };
  const receipts = new Map<string, { body: string; reply: string }>();
  const copy = (parent = false) => {
    const snapshot = structuredClone(state);
    if (!parent) delete snapshot.forgotten;
    return snapshot;
  };
  const scope = (id: string) => {
    if (id !== 'preview-kid') throw new ApiError(403, 'FORBIDDEN', 'Not this workspace');
  };
  const check = (id: string, revision: number) => {
    scope(id);
    if (revision !== state.revision)
      throw new ApiError(409, 'COMPANION_REVISION_CONFLICT', 'Details changed');
  };
  const changed = (parent = false) => {
    state = { ...state, revision: state.revision + 1 };
    return copy(parent);
  };
  let voiceState: BuddyVoiceSettings = {
    revision: 0,
    available: contractFixtures,
    speech_enabled: true,
    transcription_enabled: true,
    voice: 'gentle',
    max_recording_seconds: 20,
    voices: [
      { value: 'gentle', label: 'Gentle', description: 'Soft and unhurried' },
      { value: 'playful', label: 'Playful', description: 'Bright and friendly' },
      { value: 'calm', label: 'Calm', description: 'Clear and reassuring' },
    ],
  };
  const audioFile = async (name: string) => {
    const response = await fetch('/media/companion/voice-casting/' + name + '.mp3');
    if (!response.ok) throw new ApiError(503, 'VOICE_UNAVAILABLE', 'Voice unavailable');
    const bytes = new Uint8Array(await response.arrayBuffer());
    let binary = '';
    for (let n = 0; n < bytes.length; n += 8192)
      binary += String.fromCharCode(...bytes.subarray(n, n + 8192));
    return { audio_base64: btoa(binary), mime_type: 'audio/mpeg' };
  };
  return {
    purge: async (id, revision, memoryId) => {
      check(id, revision);
      state.memories = state.memories.filter((m) => m.id !== memoryId);
      state.forgotten = state.forgotten?.filter((m) => m.id !== memoryId);
      return changed(true);
    },
    voice: async (id) => {
      scope(id);
      return structuredClone(voiceState);
    },
    saveVoice: async (id, revision, value) => {
      scope(id);
      if (revision !== voiceState.revision)
        throw new ApiError(409, 'COMPANION_VOICE_CHANGED', 'Changed');
      voiceState = { ...voiceState, ...value, revision: revision + 1 };
      return structuredClone(voiceState);
    },
    speak: async (id, input, signal) => {
      check(id, input.memory_revision);
      if (!contractFixtures)
        throw new ApiError(503, 'COMPANION_VOICE_NOT_ENABLED', 'Audio unavailable');
      if (signal.aborted) throw new DOMException('Stopped', 'AbortError');
      if (!voiceState.speech_enabled)
        throw new ApiError(403, 'COMPANION_VOICE_PERMISSION_REQUIRED', 'Voice off');
      if (input.voice_revision !== voiceState.revision)
        throw new ApiError(409, 'COMPANION_VOICE_CHANGED', 'Changed');
      if (input.sample === 'hello') return audioFile(voiceState.voice);
      if (input.speech_token === 'author-dynamic') return audioFile('dynamic-reply');
      throw new ApiError(403, 'COMPANION_SPEECH_TICKET_INVALID', 'Choose a spoken reply');
    },
    transcribe: async () => {
      throw new ApiError(503, 'COMPANION_TRANSCRIPTION_UNAVAILABLE', 'Transcription unavailable');
    },
    read: async (id, parent = false) => {
      scope(id);
      return copy(parent);
    },
    profile: async (id, revision, profile) => {
      check(id, revision);
      state.profile = structuredClone(profile);
      return changed();
    },
    settings: async (id, revision, enabled) => {
      check(id, revision);
      state.memory_enabled = enabled;
      return changed(true);
    },
    remember: async (id, revision, memoryId, input, parent = false) => {
      check(id, revision);
      if (!state.memory_enabled)
        throw new ApiError(403, 'COMPANION_MEMORY_DISABLED', 'Remembering is off');
      const memory = memoryInputSchema.parse(input);
      if (/@|password|phone number|address|\b\d{7,}\b/i.test(memory.text))
        throw new ApiError(400, 'COMPANION_PRIVATE_DETAIL', 'Keep private details out');
      if (
        state.memories.some(
          (m) => m.id !== memoryId && m.text.toLowerCase() === memory.text.toLowerCase(),
        )
      )
        throw new ApiError(409, 'COMPANION_MEMORY_DUPLICATE', 'Already remembered');
      const previous = state.memories.find((m) => m.id === memoryId);
      const now = new Date().toISOString();
      state.memories = [
        ...state.memories.filter((m) => m.id !== memoryId),
        {
          ...memory,
          id: memoryId,
          source: 'explicit',
          created_at: previous?.created_at || now,
          updated_at: now,
          forgotten_at: null,
        },
      ];
      return changed(parent);
    },
    forget: async (id, revision, memoryId, parent = false) => {
      check(id, revision);
      const found = state.memories.find((m) => m.id === memoryId);
      if (!found) throw new ApiError(404, 'NOT_FOUND', 'Memory not found');
      state.forgotten = [
        ...(state.forgotten || []),
        { ...found, forgotten_at: new Date().toISOString() },
      ];
      state.memories = state.memories.filter((m) => m.id !== memoryId);
      return changed(parent);
    },
    restore: async (id, revision, memoryId) => {
      check(id, revision);
      if (!state.memory_enabled)
        throw new ApiError(403, 'COMPANION_MEMORY_DISABLED', 'Remembering is off');
      const found = state.forgotten?.find((m) => m.id === memoryId);
      if (!found) throw new ApiError(404, 'NOT_FOUND', 'Memory not found');
      state.memories = [...state.memories, { ...found, forgotten_at: null }];
      state.forgotten = state.forgotten?.filter((m) => m.id !== memoryId);
      return changed(true);
    },
    price: async (id) => {
      scope(id);
      if (!contractFixtures)
        throw new ApiError(503, 'COMPANION_AI_UNAVAILABLE', 'Chat unavailable');
      return { stars: 0 };
    },
    chat: async (id: string, input: BuddyChatInput, signal: AbortSignal) => {
      scope(id);
      if (!contractFixtures)
        throw new ApiError(503, 'COMPANION_AI_UNAVAILABLE', 'Chat unavailable');
      if (signal.aborted) throw new DOMException('Stopped', 'AbortError');
      const body = JSON.stringify(input),
        receipt = receipts.get(input.request_id);
      if (receipt) {
        if (receipt.body !== body) throw new ApiError(409, 'CONFLICT', 'Request changed');
        return { reply: receipt.reply };
      }
      if (input.memory_revision !== state.revision)
        throw new ApiError(409, 'COMPANION_CONTEXT_CHANGED', 'Memory changed');
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, 300);
        signal.addEventListener(
          'abort',
          () => {
            clearTimeout(timer);
            reject(new DOMException('Stopped', 'AbortError'));
          },
          { once: true },
        );
      });
      const reply = /story/i.test(input.text)
        ? COMPLETE_BUDDY_STORY
        : input.pageContext.page !== 'home'
          ? 'We can look at ' +
            (input.pageContext.title || 'this page') +
            ' together. Which part would you like to understand first? You can describe one small thing, and we can take it slowly.'
          : /tired|sad|rest|quiet|pause/i.test(input.text)
            ? 'We can slow down. You do not need to finish anything. Would you like a quiet moment, or would you like to tell me a little more?'
            : 'I am right here. We can try one little step together, or take a break whenever you like.';
      receipts.set(input.request_id, { body, reply });
      return {
        reply,
        speech_token: reply.startsWith('I am right here.') ? 'author-dynamic' : undefined,
      };
    },
  };
}
const spaceOptions = [
  { key: 'room', title: 'Chat room', detail: 'A little time with your buddy', icon: MessageCircle },
  {
    key: 'page',
    title: 'Create together',
    detail: 'Explore your ideas and stories',
    icon: Palette,
  },
  { key: 'parent', title: 'Grown-ups', detail: 'Memories and family settings', icon: ShieldCheck },
];
function MobileCompanionNavigation({
  view,
  onChange,
}: {
  view: string;
  onChange: (view: string) => void;
}) {
  useUiLocale();
  const [open, setOpen] = useState(false),
    root = useRef<HTMLDivElement>(null),
    trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  return (
    <div className="companion-mobile-navigation" ref={root}>
      <button
        type="button"
        className="companion-space-trigger"
        ref={trigger}
        aria-label={t('Switch space')}
        aria-expanded={open}
        aria-controls="companion-space-menu"
        onClick={() => setOpen((value) => !value)}
      >
        {t(spaceOptions.find((option) => option.key === view)?.title || 'Chat room')}
        <ChevronDown size={15} aria-hidden="true" />
      </button>
      {open && (
        <nav
          id="companion-space-menu"
          className="companion-space-menu"
          aria-label={t('Companion spaces')}
        >
          {spaceOptions.map(({ key, title, detail, icon: Icon }) => (
            <button
              type="button"
              key={key}
              aria-current={view === key ? 'page' : undefined}
              onClick={() => {
                onChange(key);
                setOpen(false);
                trigger.current?.focus();
              }}
            >
              <span className="companion-space-icon">
                <Icon size={19} aria-hidden="true" />
              </span>
              <span>
                <strong>{t(title)}</strong>
                <small>{t(detail)}</small>
              </span>
              {view === key && <Check size={16} aria-hidden="true" />}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
export function CompanionPreview() {
  useUiLocale();
  const [params, setParams] = useSearchParams(),
    view = params.get('view') || 'room';
  // Internal browser QA faults are restricted to this isolated adapter.
  const check = params.get('check');
  const client = useMemo(() => {
    const api =
      import.meta.env.VITE_COMPANION_REVIEW_DATABASE === 'true'
        ? createCompanionReviewApi(check === 'history-pages')
        : createCompanionPreviewApi();
    if (check === 'read-error')
      api.read = async () => {
        throw new Error('Unavailable');
      };
    if (check === 'chat-error')
      api.chat = async () => {
        throw new Error('Unavailable');
      };
    return api;
  }, [check]);
  return (
    <div className="flex h-dvh flex-col bg-canvas">
      <header className="companion-shell-header">
        <img src="/logo-black-horizontal-approved-20261021.png" alt="Airbotix" />
        <nav className="companion-desktop-nav" aria-label={t('Companion spaces')}>
          {spaceOptions.map(({ key, title }) => (
            <button
              key={key}
              onClick={() => setParams(key === 'room' ? {} : { view: key })}
              aria-current={view === key ? 'page' : undefined}
              className={view === key ? 'btn-pill-primary' : 'btn-pill-ghost'}
            >
              {t(title)}
            </button>
          ))}
        </nav>
        {(view === 'parent' || view === 'page') && <LanguageSwitcher />}
        <MobileCompanionNavigation
          view={view}
          onChange={(key) => setParams(key === 'room' ? {} : { view: key })}
        />
      </header>
      <main className="min-h-0 flex-1">
        <CompanionSession
          key={view === 'parent' ? 'parent' : 'kid'}
          kidId="preview-kid"
          audience={view === 'parent' ? 'parent' : 'kid'}
          client={client}
          initialMessages={view === 'parent' ? [] : initialMessages}
          pageOverride={
            view === 'page'
              ? {
                  page: 'studio',
                  title: 'A story for the moon rover',
                  summary:
                    'A completed four-part story about listening, following clues and trying together.',
                }
              : { page: 'home' }
          }
        >
          {view === 'design' ? (
            <CompanionDesign />
          ) : view === 'parent' ? (
            <div className="h-full overflow-auto p-6">
              <CompanionParentControls showLanguage={false} />
            </div>
          ) : view === 'page' ? (
            <div className="mx-auto h-full max-w-4xl overflow-auto p-6">
              <h1 className="section-heading">The Moon Rover’s Pocket Map</h1>
              <article className="card mt-5 whitespace-pre-line p-7 leading-loose">
                {COMPLETE_BUDDY_STORY}
              </article>
              <CompanionHelp />
            </div>
          ) : (
            <CompanionRoom />
          )}
        </CompanionSession>
      </main>
    </div>
  );
}
